import { Box, Container, Typography, Grid, Card, CardContent, Paper, Chip, useTheme, useMediaQuery } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Button from "@mui/material/Button";

export default function H2AVisaPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    return (
        <Box sx={{ 
            minHeight: '100vh',
            backgroundColor: '#f8f9fa',
            paddingTop: '80px' // Account for fixed navbar
        }}>
            <Container maxWidth="lg" sx={{ py: 4 }}>
                {/* Header Section */}
                <Box sx={{ textAlign: 'center', mb: 6 }}>
                    <Typography 
                        variant={isMobile ? "h3" : "h2"} 
                        color="primary" 
                        gutterBottom
                        sx={{ fontWeight: 700, mb: 2 }}
                    >
                        {t('h2aVisa.title', 'H2-A Visa')}
                    </Typography>
                    <Typography 
                        variant={isMobile ? "h6" : "h5"} 
                        color="text.secondary"
                        sx={{ maxWidth: 800, mx: 'auto', lineHeight: 1.6 }}
                    >
                        {t('h2aVisa.subtitle', 'Temporary Agricultural Workers Program - Connecting skilled agricultural workers with American farms')}
                    </Typography>
                </Box>

                {/* Main Content Grid */}
                <Grid container spacing={4}>
                    {/* What is H2-A Visa */}
                    <Grid item xs={12}>
                        <Card sx={{ boxShadow: '0 4px 20px rgba(0,0,0,0.1)', mb: 4 }}>
                            <CardContent sx={{ p: 4 }}>
                                <Typography variant="h4" color="primary" gutterBottom sx={{ fontWeight: 600, mb: 3, textAlign: 'center' }}>
                                    {t('h2aVisa.whatIsTitle', 'What is the H2-A Visa?')}
                                </Typography>
                                <Typography variant="body1" paragraph sx={{ lineHeight: 1.7, mb: 2, textAlign: 'center', maxWidth: 800, mx: 'auto' }}>
                                    {t('h2aVisa.whatIsDescription1', 'The H2-A visa is a non-immigrant visa that allows U.S. employers to bring foreign nationals to the United States to fill temporary agricultural jobs.')}
                                </Typography>
                                <Typography variant="body1" paragraph sx={{ lineHeight: 1.7, mb: 2, textAlign: 'center', maxWidth: 800, mx: 'auto' }}>
                                    {t('h2aVisa.whatIsDescription2', 'This program is specifically designed to address seasonal labor shortages in the agricultural sector, ensuring that American farms can maintain their productivity during peak harvest seasons.')}
                                </Typography>
                                <Typography variant="body1" sx={{ lineHeight: 1.7, textAlign: 'center', maxWidth: 800, mx: 'auto' }}>
                                    {t('h2aVisa.whatIsDescription3', 'The H2-A visa is renewable and allows workers to stay in the U.S. for up to one year, with the possibility of extensions for up to three years.')}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Key Benefits */}
                    <Grid item xs={12}>
                        <Card sx={{ boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
                            <CardContent sx={{ p: 4 }}>
                                <Typography variant="h4" color="primary" gutterBottom sx={{ fontWeight: 600, mb: 4, textAlign: 'center' }}>
                                    {t('h2aVisa.benefitsTitle', 'Key Benefits of the H2-A Visa Program')}
                                </Typography>
                                
                                <Grid container spacing={3}>
                                    <Grid item xs={12} sm={6} md={3}>
                                        <Box sx={{ textAlign: 'center', p: 2 }}>
                                            <Chip 
                                                label={t('h2aVisa.benefit1', 'Legal Work')} 
                                                color="primary" 
                                                sx={{ mb: 2, fontSize: '1rem', p: 1 }}
                                            />
                                            <Typography variant="body2" color="text.secondary">
                                                {t('h2aVisa.benefit1Desc', 'Work legally in the U.S. with proper documentation')}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                    
                                    <Grid item xs={12} sm={6} md={3}>
                                        <Box sx={{ textAlign: 'center', p: 2 }}>
                                            <Chip 
                                                label={t('h2aVisa.benefit2', 'Fair Wages')} 
                                                color="primary" 
                                                sx={{ mb: 2, fontSize: '1rem', p: 1 }}
                                            />
                                            <Typography variant="body2" color="text.secondary">
                                                {t('h2aVisa.benefit2Desc', 'Guaranteed fair wages and working conditions')}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                    
                                    <Grid item xs={12} sm={6} md={3}>
                                        <Box sx={{ textAlign: 'center', p: 2 }}>
                                            <Chip 
                                                label={t('h2aVisa.benefit3', 'Housing')} 
                                                color="primary" 
                                                sx={{ mb: 2, fontSize: '1rem', p: 1 }}
                                            />
                                            <Typography variant="body2" color="text.secondary">
                                                {t('h2aVisa.benefit3Desc', 'Employer-provided housing or housing allowance')}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                    
                                    <Grid item xs={12} sm={6} md={3}>
                                        <Box sx={{ textAlign: 'center', p: 2 }}>
                                            <Chip 
                                                label={t('h2aVisa.benefit4', 'Transportation')} 
                                                color="primary" 
                                                sx={{ mb: 2, fontSize: '1rem', p: 1 }}
                                            />
                                            <Typography variant="body2" color="text.secondary">
                                                {t('h2aVisa.benefit4Desc', 'Transportation to and from the U.S. included')}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* How Laboria Helps */}
                    <Grid item xs={12}>
                        <Paper 
                            sx={{ 
                                p: 4, 
                                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                                color: 'white',
                                textAlign: 'center',
                                mb: 4 
                            }}
                        >
                            <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                                {t('h2aVisa.howWeHelpTitle', 'How Laboria Helps with H2-A Visa Processing')}
                            </Typography>
                            <Typography variant="h6" sx={{ mb: 4, opacity: 0.9 }}>
                                {t('h2aVisa.howWeHelpSubtitle', 'We handle the entire process from start to finish')}
                            </Typography>
                            
                            <Grid container spacing={3} justifyContent="center">
                                <Grid item xs={12} sm={6} md={3}>
                                    <Box sx={{ p: 2 }}>
                                        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                                            {t('h2aVisa.step1', '1. Application')}
                                        </Typography>
                                        <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                            {t('h2aVisa.step1Desc', 'Complete visa application and documentation')}
                                        </Typography>
                                    </Box>
                                </Grid>
                                
                                <Grid item xs={12} sm={6} md={3}>
                                    <Box sx={{ p: 2 }}>
                                        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                                            {t('h2aVisa.step2', '2. Processing')}
                                        </Typography>
                                        <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                            {t('h2aVisa.step2Desc', 'Handle all government processing and approvals')}
                                        </Typography>
                                    </Box>
                                </Grid>
                                
                                <Grid item xs={12} sm={6} md={3}>
                                    <Box sx={{ p: 2 }}>
                                        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                                            {t('h2aVisa.step3', '3. Support')}
                                        </Typography>
                                        <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                            {t('h2aVisa.step3Desc', 'Ongoing support throughout the process')}
                                        </Typography>
                                    </Box>
                                </Grid>
                                
                                <Grid item xs={12} sm={6} md={3}>
                                    <Box sx={{ p: 2 }}>
                                        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                                            {t('h2aVisa.step4', '4. Success')}
                                        </Typography>
                                        <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                            {t('h2aVisa.step4Desc', 'Ensure successful visa approval and arrival')}
                                        </Typography>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Paper>
                    </Grid>
                </Grid>

                {/* Client Section */}
                <Grid item xs={12}>
                    <Card sx={{ boxShadow: '0 4px 20px rgba(0,0,0,0.1)', mb: 4 }}>
                        <CardContent sx={{ p: 4 }}>
                            <Typography variant="h3" color="primary" gutterBottom sx={{ fontWeight: 700, mb: 4, textAlign: 'center' }}>
                                {t('h2aVisa.clientSectionTitle', 'For U.S. Employers & Agricultural Businesses')}
                            </Typography>
                            
                            <Grid container spacing={4}>
                                <Grid item xs={12} md={6}>
                                    <Typography variant="h5" color="secondary" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                                        {t('h2aVisa.clientBenefitsTitle', 'Why Choose H2-A Workers?')}
                                    </Typography>
                                    <Box component="ul" sx={{ pl: 2 }}>
                                        <Typography component="li" variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
                                            {t('h2aVisa.clientBenefit1', 'Reliable seasonal workforce during peak harvest times')}
                                        </Typography>
                                        <Typography component="li" variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
                                            {t('h2aVisa.clientBenefit2', 'Skilled workers with agricultural experience')}
                                        </Typography>
                                        <Typography component="li" variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
                                            {t('h2aVisa.clientBenefit3', 'Compliance with U.S. labor laws and regulations')}
                                        </Typography>
                                        <Typography component="li" variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
                                            {t('h2aVisa.clientBenefit4', 'Flexible employment periods (up to 3 years)')}
                                        </Typography>
                                    </Box>
                                </Grid>
                                
                                <Grid item xs={12} md={6}>
                                    <Typography variant="h5" color="secondary" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                                        {t('h2aVisa.clientRequirementsTitle', 'Employer Requirements')}
                                    </Typography>
                                    <Box component="ul" sx={{ pl: 2 }}>
                                        <Typography component="li" variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
                                            {t('h2aVisa.clientRequirement1', 'Demonstrate temporary or seasonal need for workers')}
                                        </Typography>
                                        <Typography component="li" variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
                                            {t('h2aVisa.clientRequirement2', 'Pay prevailing wage rates and provide housing')}
                                        </Typography>
                                        <Typography component="li" variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
                                            {t('h2aVisa.clientRequirement3', 'Cover transportation costs to and from the U.S.')}
                                        </Typography>
                                        <Typography component="li" variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
                                            {t('h2aVisa.clientRequirement4', 'Ensure workers\' compensation coverage')}
                                        </Typography>
                                    </Box>
                                </Grid>
                            </Grid>
                            
                            <Box sx={{ textAlign: 'center', mt: 4 }}>
                                <Button 
                                    variant="contained" 
                                    size="large"
                                    onClick={() => {
                                        navigate('/contact');
                                        window.scrollTo({ top: 0, behavior: 'smooth' });
                                    }}
                                    sx={{ 
                                        backgroundColor: theme.palette.secondary.main,
                                        fontSize: '1.1rem',
                                        padding: '12px 32px',
                                        '&:hover': {
                                            backgroundColor: theme.palette.secondary.dark
                                        }
                                    }}
                                >
                                    {t('h2aVisa.clientCtaButton', 'Hire H2-A Workers')}
                                </Button>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Applicant Section */}
                <Grid item xs={12}>
                    <Card sx={{ boxShadow: '0 4px 20px rgba(0,0,0,0.1)', mb: 4 }}>
                        <CardContent sx={{ p: 4 }}>
                            <Typography variant="h3" color="primary" gutterBottom sx={{ fontWeight: 700, mb: 4, textAlign: 'center' }}>
                                {t('h2aVisa.applicantSectionTitle', 'For Agricultural Workers Seeking U.S. Employment')}
                            </Typography>
                            
                            <Grid container spacing={4}>
                                <Grid item xs={12} md={6}>
                                    <Typography variant="h5" color="secondary" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                                        {t('h2aVisa.applicantBenefitsTitle', 'Benefits for Workers')}
                                    </Typography>
                                    <Box component="ul" sx={{ pl: 2 }}>
                                        <Typography component="li" variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
                                            {t('h2aVisa.applicantBenefit1', 'Legal work authorization in the United States')}
                                        </Typography>
                                        <Typography component="li" variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
                                            {t('h2aVisa.applicantBenefit2', 'Competitive wages with fair working conditions')}
                                        </Typography>
                                        <Typography component="li" variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
                                            {t('h2aVisa.applicantBenefit3', 'Employer-provided housing or housing allowance')}
                                        </Typography>
                                        <Typography component="li" variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
                                            {t('h2aVisa.applicantBenefit4', 'Round-trip transportation included')}
                                        </Typography>
                                    </Box>
                                </Grid>
                                
                                <Grid item xs={12} md={6}>
                                    <Typography variant="h5" color="secondary" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                                        {t('h2aVisa.applicantRequirementsTitle', 'Worker Requirements')}
                                    </Typography>
                                    <Box component="ul" sx={{ pl: 2 }}>
                                        <Typography component="li" variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
                                            {t('h2aVisa.applicantRequirement1', 'Minimum 18 years of age')}
                                        </Typography>
                                        <Typography component="li" variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
                                            {t('h2aVisa.applicantRequirement2', 'Proven agricultural work experience')}
                                        </Typography>
                                        <Typography component="li" variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
                                            {t('h2aVisa.applicantRequirement3', 'Citizen of eligible treaty country')}
                                        </Typography>
                                        <Typography component="li" variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
                                            {t('h2aVisa.applicantRequirement4', 'Willingness to work seasonal agricultural jobs')}
                                        </Typography>
                                    </Box>
                                </Grid>
                            </Grid>
                            
                            <Box sx={{ textAlign: 'center', mt: 4 }}>
                                <Button 
                                    variant="contained" 
                                    size="large"
                                    onClick={() => {
                                        navigate('/openings/request/1');
                                        window.scrollTo({ top: 0, behavior: 'smooth' });
                                    }}
                                    sx={{ 
                                        backgroundColor: theme.palette.primary.main,
                                        fontSize: '1.1rem',
                                        padding: '12px 32px',
                                        '&:hover': {
                                            backgroundColor: theme.palette.primary.dark
                                        }
                                    }}
                                >
                                    {t('h2aVisa.applicantCtaButton', 'Apply for H2-A Jobs')}
                                </Button>
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                                    {t('h2aVisa.applicantCtaSubtext', 'Browse available agricultural positions and start your application')}
                                </Typography>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Container>
        </Box>
    );
}
